
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate'
import { getCountryCodeForRegionCode, getRegionCodeForCountryCode } from 'awesome-phonenumber'
import { passwordCheck, passwordProgress } from '@/config/functions'
import { EFreelancerRequiredVerfications, EAuthMethod, EAuthMode } from '@/helpers/enums'

export default {
  name: 'Signup',

  components: {
    ValidationObserver,
    ValidationProvider
  },

  layout: 'frameless',
  middleware: 'search-redirect',
  auth: false,

  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      emailAlternative: '',
      password: '',
      Other_: false,
      isLoading: false,
      country: this.$store.getters.defaultCountry,
      mobilePhone: {
        countryCode: this.$store.getters.defaultCountry,
        number: '',
        code: ''
      },
      newsletter: false,
      terms: null,
      privacy: null,
      modalNewsletter: false,
      modalTerms: false,
      modalPrivacy: false,
      showPwd: false,
      questionDialog: false,
      questionAnswer: '',
      questionCustomAnswer: '',
      socialSignUpType: '',
      socialCountry: '',
      tempSocialDialog: false,
      showTip: false,
      widgetId: null,
      isCountryMessage: false,
      isSocialCountryMessage: false,
      mobileInUse: false,
      isSubmitting: false,
      authMode: EAuthMode.SIGNUP
    }
  },

  fetch() {},

  head() {
    return {
      ...this.$headMetaTags({
        title: this.$t('talent.app.meta.signup.title')
      })
    }
  },

  computed: {
    freelancerRequiredVerifications() {
      return this.$useConfig(this.$EClientConfig.FREELANCER_REQUIRED_VERIFICATIONS)
    },

    loginMethods() {
      return this.$useConfig(this.$EClientConfig.FREELANCER_LOGIN)
    },

    socialDialog: {
      get() {
        return this.tempSocialDialog
      },
      set(val) {
        this.tempSocialDialog = val
        this.socialSignUpType = ''
        this.modalNewsletter = false
        this.modalTerms = false
        this.modalPrivacy = false
        this.socialCountry = ''
        this.$auth.$storage.removeUniversal('social_signup')
      }
    },

    question() {
      return [
        {
          name: this.$t('talent.register.question.q1'),
          value: 'search_'
        },
        {
          name: this.$t('talent.register.question.q2'),
          value: 'Social_Media_'
        },
        {
          name: this.$t('talent.register.question.q3'),
          value: 'friends_college_'
        },
        {
          name: this.$t('talent.register.question.q4'),
          value: 'recruiter_'
        },
        {
          name: this.$t('talent.register.question.q6'),
          value: 'linkedin_'
        }
      ]
    },

    passwordRequirements() {
      return [
        {
          message: this.$t('talent.register.error.characters.new'),
          validated: this.$t(`talent.register.validated.characters.new`),
          predicate: /\W/.test(this.password)
        },
        {
          message: this.$t('talent.register.error.capitals.new'),
          validated: this.$t(`talent.register.validated.capitals.new`),
          predicate: this.password.toLowerCase() !== this.password
        },
        {
          message: this.$t('talent.register.error.lowers.new'),
          validated: this.$t(`talent.register.validated.lowers.new`),
          predicate: this.password.toUpperCase() !== this.password
        },
        {
          message: this.$t('talent.register.error.digits.new'),
          validated: this.$t(`talent.register.validated.digits.new`),
          predicate: /\d/.test(this.password)
        },
        {
          message: this.$t('talent.register.error.length.new'),
          ariaLabel: this.$t(`talent.register.validated.length.new`),
          predicate: this.password.length >= 10
        }
      ]
    },

    allPasswordRequirementsMet() {
      return this.passwordRequirements.every((item) => item.predicate === true)
    },

    randomQuestion() {
      return [...Array(this.question.length)]
        .map((el, i) => Math.floor(Math.random() * i))
        .reduce((a, rv, i) => ([a[i], a[rv]] = [a[rv], a[i]]) && a, this.question)
    },

    countryNotificationInfo() {
      const client = this.$store.state.clientContext.name
      const country = this.country.toLowerCase()
      return this.$t(`talent.${client}.notification.country.${country}`)
    },

    socialCountryNotificationInfo() {
      const client = this.$store.state.clientContext.name
      const country = this.socialCountry.toLowerCase()
      return this.$t(`talent.${client}.notification.country.${country}`)
    },

    socialTypeChecker() {
      return !this.modalPrivacy || !this.modalTerms || this.socialCountry.length !== 2
    },

    notificationCountries() {
      return this.$useConfig(this.$EClientConfig.REGISTER_NOTIFICATION_COUNTRY)
    },

    requirePhoneVerification() {
      return (
        this.freelancerRequiredVerifications.mobileNumber ===
        EFreelancerRequiredVerfications.PRINCIPAL
      )
    },

    emailAsAlternative() {
      return (
        this.freelancerRequiredVerifications.email === EFreelancerRequiredVerfications.ALTERNATIVE
      )
    },
    mobileCode() {
      return getCountryCodeForRegionCode(this.mobilePhone.countryCode).toString()
    },
    mobileNumber() {
      return this.mobilePhone.number
    },
    modalAriaLabel() {
      return this.$t(`talent.signup.modal.social.${this.socialSignUpType}`)
    },
    formId() {
      return this.$getElementId(this.$route.path, 'form', {
        stepName: this.requirePhoneVerification ? EAuthMethod.MOBILE : EAuthMethod.EMAIL
      })
    },
    socialFormId() {
      return this.$getElementId(this.$route.path, 'form', {
        stepName: this.socialSignUpType
      })
    },
    signupButtons() {
      const buttons = []

      if (this.requirePhoneVerification) {
        return buttons
      }

      if (this.loginMethods.linkedin) {
        buttons.push({
          type: EAuthMethod.LINKEDIN,
          id: `${this.socialDialog ? 'socialSignUp' : 'signUpWith'}LinkedIn`,
          closeBtnId: 'closeSignupDialogBtnLinkedIn',
          signupUrl: '/v1/api/social/linkedin'
        })
      }

      if (this.loginMethods.google) {
        buttons.push({
          type: EAuthMethod.GOOGLE,
          id: `${this.socialDialog ? 'socialSignUp' : 'signUpWith'}Google`,
          closeBtnId: 'closeSignupDialogBtnGoogle',
          signupUrl: '/v1/api/social/google'
        })
      }

      if (this.loginMethods.auth0) {
        buttons.push({
          type: EAuthMethod.AUTH0,
          id: `${this.socialDialog ? 'socialSignUp' : 'signUpWith'}Auth0`,
          closeBtnId: 'closeSignupDialogBtnAuth0',
          signupUrl: '/v1/api/social/auth0'
        })
      }

      return buttons
    },
    selectedAuthMethodButton() {
      return (
        this.signupButtons.find((button) => button.type === this.socialSignUpType) || {
          signUpUrl: false,
          closeBtnId: 'modalClose'
        }
      )
    },
    emailValidationRules() {
      const isSpecialCharsAllowed = this.$useConfig(this.$EClientConfig.EMAIL_SPECIAL_CHARACTERS)
      return `required|email|uniqueEmail${!isSpecialCharsAllowed ? '|specialChars' : ''}`
    }
  },

  watch: {
    country: {
      deep: true,
      immediate: true,
      handler(val) {
        this.isCountryMessage = this.notificationCountries.includes(val) || false
      }
    },

    socialCountry: {
      deep: true,
      immediate: true,
      handler(val) {
        this.isSocialCountryMessage = this.notificationCountries.includes(val) || false
      }
    },
    mobileCode(val) {
      this.$store.commit('mobile/MOBILE_CODE', getRegionCodeForCountryCode(val))
    },
    mobileNumber(val) {
      this.$store.commit('mobile/MOBILE_NUMBER', val)
    },
    email(val) {
      this.$store.commit('EMAIL_REGISTER_TO_LOGIN', val)
    }
  },

  beforeDestroy() {
    this.$toast.show = false
  },

  created() {
    for (const key of this.pwdDisplay()) {
      extend(key.extendVal, (val) => {
        if (key.regex.test(val)) {
          return true
        }
        return key.message
      })
    }

    extend('uniquePhone', this.isMobileInUse)
  },

  mounted() {
    if (this.$store.state.clientContext.reCaptchaIsEnabled) {
      this.fireReCaptcha()
    }

    this.$store.commit('mobile/MOBILE_CODE', getRegionCodeForCountryCode(this.mobileCode))
  },

  methods: {
    async isMobileInUse(value) {
      const errorMsg = this.$t('talent.register.phone.in.use')

      try {
        const payload = {
          mobileCode: getCountryCodeForRegionCode(value.countryCode).toString(),
          mobileNumber: value.number
        }

        const { data } = await this.$api.freelancer.isMobileInUse(payload)
        this.mobileInUse = data.existingUser

        return !data.existingUser || errorMsg
      } catch (err) {
        return errorMsg
      }
    },

    makePhoneFormData(token) {
      const postObj = {
        email: this.emailAlternative,
        mobileCode: this.mobileCode,
        mobileNumber: this.mobileNumber,
        firstName: this.firstName,
        lastName: this.lastName,
        residenceCountry: this.country,
        agreeToTerms: this.terms,
        agreeToPrivacy: this.privacy,
        subscribed: this.newsletter,
        preferredLocale: this.$i18n.locale
      }

      if (token) {
        postObj.reCaptchaToken = token
      }

      return postObj
    },

    makeEmailFormData(token) {
      const postObj = {
        firstName: this.firstName,
        lastName: this.lastName,
        clientId: this.$store.state.clientContext.id,
        email: this.email,
        password: this.password,
        subscribed: this.newsletter,
        agreeToTerms: this.terms,
        agreeToPrivacy: this.privacy,
        residenceCountry: this.country,
        preferredLocale: this.$i18n.locale
      }

      if (token) {
        postObj.reCaptchaToken = token
      }

      return postObj
    },

    async submitForm() {
      this.isSubmitting = true
      const isValid = await this.$refs.registerObserver.validate()

      if (!isValid) {
        this.isSubmitting = false
        return
      }

      await this.$pushEvent('signUpButtonClick')

      if (!this.$store.state.clientContext.reCaptchaIsEnabled) {
        if (this.requirePhoneVerification) {
          const payload = this.makePhoneFormData()
          await this.signUpWithPhone(payload)
          this.isSubmitting = false
        } else {
          const payload = this.makeEmailFormData()
          await this.signUpWithEmail(payload)
          this.isSubmitting = false
        }
      } else {
        window.grecaptcha.execute()
      }
    },

    async signUpWithEmail(formData) {
      try {
        this.isLoading = true
        await this.$api.freelancer.registerFreelancer(formData)
        await this.$pushEvent('signUpSuccessful', { userType: 'new' })
        // this.questionDialog = true
        await this.onLogin()
        this.isLoading = false
      } catch (err) {
        await this.$pushEvent('signUpFailed', { userType: 'new' })
        console.error('>>> Sign up failed', err)
        this.isLoading = false
      }
    },

    async signUpWithPhone(formData) {
      try {
        this.isLoading = true
        await this.$api.freelancer.registerFromPhone(formData)

        this.$router.push(`mobile-activation/${this.mobileCode}${this.mobileNumber}`)
        this.isLoading = false
      } catch (err) {
        await this.$pushEvent('signUpFailed')
        this.isLoading = false
      }
    },

    async refferalSubmit(getAnswer) {
      const postData = {
        email: this.email,
        referral: getAnswer
      }

      try {
        this.isLoading = true
        await this.$api.freelancer.setFreelancerReferral(postData)
        await this.onLogin()
        this.isLoading = false
      } catch (err) {
        this.isLoading = false
      }
    },

    async onLogin() {
      await this.$auth.loginWith('local', {
        data: {
          email: this.email,
          password: this.password
        }
      })

      await this.$getBrowserUUID()

      await this.$api.freelancer.agreeToUpdatedPolicies({
        terms: true,
        privacy: true
      })

      // this.$toast.fire(this.$t('talent.message.generic.success'))

      this.$router.push(this.$link.onboarding())
    },

    socialSignUp(network) {
      this.socialDialog = true
      this.socialSignUpType = network
      this.$auth.$storage.removeUniversal('social_signup')
    },

    onSocialSignup() {
      const dataObj = {
        residenceCountry: this.socialCountry,
        socialSignUpType: this.socialSignUpType,
        preferredLocale: this.$i18n.locale,
        subscribed: this.modalNewsletter,
        agreeToTerms: this.modalTerms,
        agreeToPrivacy: this.modalPrivacy
      }
      this.$auth.$storage.setUniversal('social_signup', dataObj, true)
    },

    fireReCaptcha() {
      window.grecaptcha.ready(() => {
        this.widgetId = window.grecaptcha.render('recaptchaIdentify', {
          sitekey: this.$store.state.clientContext.reCaptchaSiteKey,
          size: 'invisible',
          callback: (reCaptchaToken) => {
            if (this.requirePhoneVerification) {
              const payload = this.makePhoneFormData(reCaptchaToken)
              this.signUpWithPhone(payload)
            } else {
              const payload = this.makeEmailFormData(reCaptchaToken)
              this.signUpWithEmail(payload)
            }

            window.grecaptcha.reset(this.widgetId)
          }
        })
      })
    },

    pwdProgress(pwd) {
      return passwordProgress(this, pwd)
    },

    pwdDisplay() {
      return passwordCheck(this)
    }
  }
}
